import clsx from 'clsx';
import React, { useRef } from 'react';
import { useBoolean } from '../../hooks/useBoolean';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { noop } from '../../utils/utils';
import { typographyClassName } from '../atoms/typography';

export type DropdownOption = {
  label: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
};
type DropdownOptionPosition = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};
type Props = {
  className?: string;
  trigger: React.ReactNode;
  options: DropdownOption[];
  disabled?: boolean;
  position?: DropdownOptionPosition;
};

const toPx = (value: number | undefined) =>
  value !== undefined ? `${value}px` : undefined;

const Dropdown: React.FC<Props> = ({
  className,
  trigger,
  options,
  disabled = options.length === 0,
  position = {
    right: 0,
  },
}) => {
  const ref = useRef(null);
  const [showOptions, toggleShowOptions, setShowOptionsTo] = useBoolean();

  useOnClickOutside(ref, () => setShowOptionsTo(false));

  return (
    <div className={clsx('relative inline-block', className)} ref={ref}>
      <span
        onClick={disabled ? noop : toggleShowOptions}
        aria-disabled={disabled}
      >
        {trigger}
      </span>
      {showOptions && (
        <div
          className="absolute z-[1] box-border flex min-w-[200px] flex-col items-start overflow-auto rounded-lg border border-solid border-[#cccccc] bg-white py-2 shadow-md"
          style={{
            top: toPx(position.top),
            right: toPx(position.right),
            bottom: toPx(position.bottom),
            left: toPx(position.left),
          }}
        >
          {options.map((option, index) => (
            <a
              key={index}
              href={option.href}
              onClick={() => {
                toggleShowOptions();
                (option.onClick ?? noop)();
              }}
              className={clsx(
                typographyClassName,
                'block w-full cursor-pointer bg-white px-4 py-1 hover:bg-[#cbf0e4] hover:text-inherit',
              )}
            >
              {option.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
